import React, { useState, useEffect } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useDebouncedCallback } from 'use-debounce';
import FinancialReportsPresentational from './FinancialReportsPresentational';
import VariableRevenuesDetailPaginated from '../VariableRevenuesDetailPaginated';
import VarRevInsDetailPaginated from '../VarRevInsDetailPaginated';
import VarRevEnergyDetailPaginated from '../VarRevEnergyDetailPaginated';
import VarRevRecurrentEnergyDetailPaginated from '../VarRevRecurrentEnergyDetailPaginated';
import {
  getReports,
  createBonus,
  deleteBonus,
  updateBonus,
  addBonusPaymentDate,
  getReportsBonuses,
  getReportsVariableRevenuesTotals,
  getReportsVariableRevenuesInsurancesTotals,
  getReportsVariableRevenuesEnergyTotals,
  getReportsVariableRevenuesRecurrentEnergyTotals,
  getReportsVariableRevenuesSubAdminsTotals,
  getAllReports,
  getBalancePreviousYearsTotals,
  getReportsVariableRevenuesSignalTotals,
} from '../../../lib/fetch/reports';
import { useAuth } from '../../../commons/Auth';
import { useLoading } from '../../../commons/Loading';
import {
  useQueryParams,
  withDefault,
  StringParam,
  NumberParam,
} from 'use-query-params';
import SubAdminsRevenuesDetailPaginated from '../SubAdminsRevenuesDetailPaginated';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import { getTotalsReports } from '../../../lib/helpers/getTotalsReports';
import VarRevSignalsDetail from '../VarRevSignalsDetail';

const FinancialReports = (props) => {
  const { profile = {}, match = {} } = props;
  const { _id: profileId, type: profileType = '' } = profile;
  const [{ newToken, newIsAdmin } = {}] = useAuth();
  const [reports, setReports] = useState({
    categories: { gnet_admin: {}, supplier: {}, bonuses: { data: [] } },
    totals: {},
  });
  const alert = useAlert();
  const [t] = useTranslation('CONTACTS');
  const { showLoading, hideLoading } = useLoading();

  const [query, setQuery] = useQueryParams({
    year: withDefault(NumberParam, new Date().getFullYear()),
    number: withDefault(StringParam, ''),
  });

  const { year, number } = query;

  const path = useLocation();
  const condition = path.pathname.split('/');
  useEffect(() => {
    if (condition.length <= 4) {
      fetchReports();
    }
  }, [year, number]);

  const [fetchReports] = useDebouncedCallback(async () => {
    showLoading();
    try {
      const [
        bonusesResponse,
        variableRevenuesResponse,
        variableRevenuesInsurancesResponse,
        variableRevenuesEnergyResponse,
        variableRevenuesRecurrentEnergyResponse,
        variableRevenuesSignalsResponse,
        variableRevenuesSubAdminsResponse,
        balancePreviousYearsResponse,
      ] = await Promise.allSettled([
        getReportsBonuses({ year, number, newToken, profileId }),
        getReportsVariableRevenuesTotals({ year, number, newToken, profileId }),
        getReportsVariableRevenuesInsurancesTotals({
          year,
          number,
          newToken,
          profileId,
        }),
        getReportsVariableRevenuesEnergyTotals({
          year,
          number,
          newToken,
          profileId,
        }),
        getReportsVariableRevenuesRecurrentEnergyTotals({
          year,
          number,
          newToken,
          profileId,
        }),
        getReportsVariableRevenuesSignalTotals({
          year,
          number,
          newToken,
          profileId,
        }),
        getReportsVariableRevenuesSubAdminsTotals({
          year,
          number,
          newToken,
          profileId,
        }),
        getBalancePreviousYearsTotals({
          year,
          number,
          newToken,
          profileId,
        }),
      ]);

      const totals = getTotalsReports({
        newIsAdmin,
        bonusesResponse,
        variableRevenuesResponse,
        variableRevenuesInsurancesResponse,
        variableRevenuesEnergyResponse,
        variableRevenuesRecurrentEnergyResponse,
        variableRevenuesSubAdminsResponse,
        variableRevenuesSignalsResponse,
        balancePreviousYearsResponse,
      });

      const combinedReports = {
        categories: {
          bonuses: { data: bonusesResponse?.value?.data?.bonuses } || {
            data: [],
          },
          variableRevenues: {
            data:
              variableRevenuesResponse?.value?.data
                ?.queryYearFilteredVariableRevenues,
          } || { data: [] },
          variableRevenuesInsurance: {
            data:
              variableRevenuesInsurancesResponse?.value?.data
                ?.queryYearFilteredVariableRevenuesInsurance,
          } || { data: [] },
          variableRevenuesEnergy: {
            data:
              variableRevenuesEnergyResponse?.value?.data
                ?.queryYearFilteredVariableRevenuesEnergy,
          } || { data: [] },
          variableRevenuesRecurrentEnergy: {
            data:
              variableRevenuesRecurrentEnergyResponse?.value?.data
                ?.queryYearFilteredVariableRevenuesRecurrentEnergy,
          } || { data: [] },
          variableRevenuesSubAdmins: {
            data:
              variableRevenuesSubAdminsResponse?.value?.data
                ?.queryYearFilteredVariableRevenuesSubAdmins,
          } || { data: [] },
          variableRevenuesSignals: {
            data:
              variableRevenuesSignalsResponse?.value?.data
                ?.queryYearFilteredVariableRevenuesSignals,
          } || { data: [] },
        },
        totals,
      };

      setReports(combinedReports);
      hideLoading();
    } catch (error) {
      hideLoading();
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }
  }, 1000);

  function onFilter(queryDiff) {
    setQuery(queryDiff);
  }

  async function onAddBonus(bonus) {
    const { error } = await createBonus({
      newToken,
      bonus: { ...bonus, profileId },
    });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    fetchReports();
    alert.success(t('COMMON:SUCCESS_CREATE'));
  }

  async function onBonusDelete(_id) {
    const { error } = await deleteBonus({ newToken, _id });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    fetchReports();
    alert.success(t('COMMON:SUCCESS_DELETE'));
  }

  async function onBonusUpdate(data) {
    const { error } = await updateBonus({ newToken, data });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    fetchReports();
    alert.success(t('COMMON:SUCCESS_UPDATE'));
  }

  async function addPaymentDate(data) {
    const { error } = await addBonusPaymentDate({ newToken, data });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    fetchReports();
    alert.success(t('COMMON:SUCCESS_UPDATE'));
  }

  return (
    <Switch>
      <Route
        exact
        path={match.url}
        component={(props) => (
          <FinancialReportsPresentational
            data={reports}
            profile={profile}
            onAddBonus={onAddBonus}
            onBonusDelete={onBonusDelete}
            onBonusUpdate={onBonusUpdate}
            onFilter={onFilter}
            query={query}
            profileType={profileType}
            addPaymentDate={addPaymentDate}
            {...props}
          />
        )}
      />
      <Route
        path={`${match.url}/sub-admins-revenues`}
        component={(props) => (
          <SubAdminsRevenuesDetailPaginated
            profile={profile}
            {...props}
            onAddBonus={onAddBonus}
          />
        )}
      />
      <Route
        path={`${match.url}/variable-revenues`}
        component={(props) => (
          <VariableRevenuesDetailPaginated
            profile={profile}
            {...props}
            onAddBonus={onAddBonus}
          />
        )}
      />
      <Route
        path={`${match.url}/variable-revenues-insurances`}
        component={(props) => (
          <VarRevInsDetailPaginated
            profile={profile}
            onAddBonus={onAddBonus}
            {...props}
          />
        )}
      />

      <Route
        path={`${match.url}/variable-revenues-energy`}
        component={(props) => (
          <VarRevEnergyDetailPaginated
            profile={profile}
            onAddBonus={onAddBonus}
            {...props}
          />
        )}
      />
      <Route
        path={`${match.url}/variable-revenues-recurrent-energy`}
        component={(props) => (
          <VarRevRecurrentEnergyDetailPaginated
            profile={profile}
            onAddBonus={onAddBonus}
            {...props}
          />
        )}
      />
      <Route
        path={`${match.url}/variable-revenues-signals`}
        component={(props) => (
          <VarRevSignalsDetail
            profile={profile}
            onAddBonus={onAddBonus}
            {...props}
          />
        )}
      />
    </Switch>
  );
};

export default withRouter(FinancialReports);
